import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import CropLandscapeOutlined from "@mui/icons-material/CropLandscapeOutlined";
import CropPortraitOutlined from "@mui/icons-material/CropPortraitOutlined";
import CropSquareOutlined from "@mui/icons-material/CropSquareOutlined";
import Delete from "@mui/icons-material/Delete";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PendingOutlined from "@mui/icons-material/PendingOutlined";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import VideoLibraryOutlined from "@mui/icons-material/VideoLibraryOutlined";
import {
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import ImagePicker from "../../components/ImagePicker";
import { formatTimestamp, hostname, parseError, getSupportContactInfo } from "../../utils";
import { useWhitelabel } from "../../context/WhitelabelContext";

export default function Video({
  currentUser,
  getToken,
  eventListener,
  companyId,
}: {
  getToken: () => Promise<string>;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
}) {
  const { whitelabelSettings } = useWhitelabel();
  const [init, setInit] = useState(false);
  const [videos, setVideos] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoDialogLoading, setVideoDialogLoading] = useState(false);
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any>({});
  const [selectedModel, setSelectedModel] = useState("");
  const [videoPrompt, setVideoPrompt] = useState("");
  const [videoModels, setVideoModels] = useState<Array<any>>([]);
  const [videoDuration, setVideoDuration] = useState(4);
  const [videoAspectRatio, setVideoAspectRatio] = useState("16:9");
  const [videoResolution, setVideoResolution] = useState("1080p");
  const [motionIntensity, setMotionIntensity] = useState("medium");
  const [cameraMotion, setCameraMotion] = useState("static");
  const [videoStyle, setVideoStyle] = useState("realistic");
  const [hasGeneratingVideos, setHasGeneratingVideos] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVideoForMenu, setSelectedVideoForMenu] = useState<any>(null);

  // Frame selection states for first-last-frame models
  const [firstFrameUrl, setFirstFrameUrl] = useState("");
  const [firstFrameId, setFirstFrameId] = useState("");
  const [lastFrameUrl, setLastFrameUrl] = useState("");
  const [lastFrameId, setLastFrameId] = useState("");
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [selectingFrame, setSelectingFrame] = useState<"first" | "last" | "single" | null>(null);

  // Image selection for image-to-video models
  const [singleImageUrl, setSingleImageUrl] = useState("");
  const [singleImageId, setSingleImageId] = useState("");

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteVideo = async (e: any, videoId: string) => {
    const currentToken = await getToken();
    setLoading(true);
    axios
      .delete(`${hostname}/videos/${videoId}`, {
        params: {
          companyId,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        getData();
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const previewVideo = (e: any, video: any) => {
    setSelectedVideo(video);
    setVideoPreviewOpen(true);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    video: any
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedVideoForMenu(video);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVideoForMenu(null);
  };

  const handlePreviewFromMenu = () => {
    if (selectedVideoForMenu) {
      previewVideo(null, selectedVideoForMenu);
    }
    handleMenuClose();
  };

  const handleDeleteFromMenu = () => {
    if (selectedVideoForMenu) {
      deleteVideo(null, selectedVideoForMenu.id);
    }
    handleMenuClose();
  };

  const handleDownloadFromMenu = async () => {
    if (selectedVideoForMenu && selectedVideoForMenu.url) {
      try {
        // Fetch the video as a blob to force download
        const response = await fetch(selectedVideoForMenu.url);
        if (!response.ok) {
          throw new Error("Failed to download video");
        }
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `video_${selectedVideoForMenu.id}.mp4`;
        link.click();
        window.URL.revokeObjectURL(downloadUrl);

        eventListener({
          type: "SET_SUCCESS",
          message: "Video downloaded successfully",
        });
      } catch (error) {
        console.error("Error downloading video:", error);
        eventListener({
          type: "SET_ERROR",
          error: "Failed to download video",
        });
      }
    }
    handleMenuClose();
  };

  const handleModelChange = (modelId: string) => {
    setSelectedModel(modelId);
    // Reset prompt when switching models
    setVideoPrompt("");
    // Reset frame selections when switching models
    setFirstFrameUrl("");
    setFirstFrameId("");
    setLastFrameUrl("");
    setLastFrameId("");
    setSingleImageUrl("");
    setSingleImageId("");

    // Set duration to first valid duration for the model
    const model = videoModels.find(m => m.id === modelId);
    if (model && model.durations && model.durations.length > 0) {
      const firstDuration = parseInt(model.durations[0].replace('s', ''));
      setVideoDuration(firstDuration);
    }
  };

  const handleOpenImagePicker = (frameType: "first" | "last" | "single") => {
    setSelectingFrame(frameType);
    setImagePickerOpen(true);
  };

  const handleImageSelect = (imageUrl: string, imageId?: string) => {
    if (selectingFrame === "first") {
      setFirstFrameUrl(imageUrl);
      setFirstFrameId(imageId || "");
    } else if (selectingFrame === "last") {
      setLastFrameUrl(imageUrl);
      setLastFrameId(imageId || "");
    } else if (selectingFrame === "single") {
      setSingleImageUrl(imageUrl);
      setSingleImageId(imageId || "");
    }
    setImagePickerOpen(false);
    setSelectingFrame(null);
  };

  const getFrameUrlsFromVideo = (video: any) => {
    // Extract frame URLs from parameters JSONB field
    if (video.parameters) {
      return {
        firstFrameUrl: video.parameters.first_frame_url,
        lastFrameUrl: video.parameters.last_frame_url
      };
    }
    return { firstFrameUrl: null, lastFrameUrl: null };
  };

  const requiresFrames = () => {
    const model = videoModels.find(m => m.id === selectedModel);
    return model?.requires_frames === true;
  };

  const requiresImage = () => {
    const model = videoModels.find(m => m.id === selectedModel);
    return model?.requires_image === true;
  };

  const calculateCreditCost = () => {
    const model = videoModels.find(m => m.id === selectedModel);
    if (!model) return 0;
    const baseCost = model.cost_credits || 100;
    // Base cost is for the first available duration, +5 credits per additional second
    const minDuration = model.durations && model.durations.length > 0
      ? parseInt(model.durations[0].replace('s', ''))
      : 4;
    const additionalSeconds = Math.max(0, videoDuration - minDuration);
    // Total cost = base cost + (5 × additional seconds)
    return baseCost + (additionalSeconds * 5);
  };

  const copyPromptToClipboard = async (prompt: string) => {
    try {
      await navigator.clipboard.writeText(prompt);
      eventListener({
        type: "SET_SUCCESS",
        message: "Prompt copied to clipboard!",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        message: "Failed to copy prompt",
      });
    }
  };


  const createVideo = async () => {
    const currentToken = await getToken();
    setVideoDialogLoading(true);

    // Validate required fields
    if (!selectedModel || !videoPrompt.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "Please select a model and enter a prompt",
      });
      setVideoDialogLoading(false);
      return;
    }

    // Validate frame requirements for first-last-frame models
    if (requiresFrames() && (!firstFrameUrl || !lastFrameUrl)) {
      eventListener({
        type: "SET_ERROR",
        error: "Please select both first and last frame images for this model",
      });
      setVideoDialogLoading(false);
      return;
    }

    // Validate image requirement for image-to-video models
    if (requiresImage() && !singleImageUrl) {
      eventListener({
        type: "SET_ERROR",
        error: "Please select an image for this model",
      });
      setVideoDialogLoading(false);
      return;
    }

    const req: any = {
      companyId: companyId,
      prompt: videoPrompt,
      model: selectedModel,
      duration: videoDuration,
      aspect_ratio: videoAspectRatio,
    };

    // Add frame URLs for first-last-frame models
    if (requiresFrames()) {
      req.first_frame_url = firstFrameUrl;
      req.last_frame_url = lastFrameUrl;
      if (firstFrameId) req.first_frame_id = firstFrameId;
      if (lastFrameId) req.last_frame_id = lastFrameId;
    }

    // Add image URL for image-to-video models
    if (requiresImage()) {
      req.image_url = singleImageUrl;
      if (singleImageId) req.image_id = singleImageId;
    }

    // Add resolution for Veo 3.1 Lite
    if (selectedModel === "fal-ai/veo3.1/lite") {
      req.resolution = videoResolution;
    }

    // Add Kling-specific parameters
    if (selectedModel?.includes("kling")) {
      req.motion_intensity = motionIntensity;
      req.camera_motion = cameraMotion;
      req.style = videoStyle;
    }

    axios
      .post(`${hostname}/videos`, req, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then(() => {
        getData();
        setVideoPrompt("");
        setSelectedModel("");
        setVideoDuration(4);
        setVideoAspectRatio("16:9");
        setVideoResolution("1080p");
        setMotionIntensity("medium");
        setCameraMotion("static");
        setVideoStyle("realistic");
        setFirstFrameUrl("");
        setFirstFrameId("");
        setLastFrameUrl("");
        setLastFrameId("");
        setSingleImageUrl("");
        setSingleImageId("");
        eventListener({
          type: "SET_SUCCESS",
          message: "Video generation started. Check back in a few minutes.",
        });
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setVideoDialogLoading(false);
        setVideoDialogOpen(false);
      });
  };

  // Helper to get valid duration for a model
  const getValidDurationForModel = (model: any): number => {
    if (model && model.durations && model.durations.length > 0) {
      return parseInt(model.durations[0].replace('s', ''));
    }
    return 4; // Default fallback
  };

  const getVideoModels = async () => {
    const currentToken = await getToken();
    try {
      const response = await axios.get(`${hostname}/api/v0/videos/models`, {
        params: {
          companyId,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setVideoModels(response.data);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };


  const getData = async () => {
    const currentToken = await getToken();
    setLoading(true);
    axios
      .get(`${hostname}/videos`, {
        params: {
          companyId,
          page: page + 1,
          pageSize,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setVideos(res.data.items);
        setTotal(res.data.total);
        setHasGeneratingVideos(
          res.data.items.some((v: any) => v.status === "generating")
        );
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case "completed":
        return <Chip label="Completed" color="primary" size="small" />;
      case "generating":
        return <Chip label="Generating" color="warning" size="small" />;
      case "failed":
        return <Chip label="Failed" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  useEffect(() => {
    if (currentUser.uid) {
      getData();
      getVideoModels();
    }
  }, [currentUser, companyId, page]);

  // Set default model selection and valid duration
  useEffect(() => {
    if (videoModels.length > 0 && !selectedModel) {
      // Default to Google Veo 3.1 if available
      const veo3Model = videoModels.find(m => m.id === "fal-ai/veo3.1/fast");
      const defaultModel = veo3Model || videoModels[0];
      if (defaultModel) {
        setSelectedModel(defaultModel.id);
        // Set duration to first valid duration for the model
        setVideoDuration(getValidDurationForModel(defaultModel));
      }
    }
  }, [videoModels]);

  return (
    <Box>
      <div style={{ width: "100%", minHeight: 40 }}>
        <Button
          data-tutorial="create-video"
          variant="contained"
          style={{ marginTop: 10 }}
          startIcon={<VideoLibraryOutlined />}
          onClick={() => setVideoDialogOpen(true)}
        >
          Create Video
        </Button>
        <IconButton
          onClick={() => getData()}
          style={{ float: "right", marginTop: 10 }}
        >
          <RefreshOutlined />
        </IconButton>
        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </div>
      {videos.length == 0 && !loading && (
        <Box sx={{ mt: 3 }}>
          <Typography>No videos found.</Typography>
        </Box>
      )}
      <TableContainer data-tutorial="video-list">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Preview</TableCell>
              <TableCell>Prompt</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {videos.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  {item.status === "completed" && item.url ? (
                    <Box
                      sx={{
                        width: 80,
                        height: 45,
                        borderRadius: 1,
                        overflow: "hidden",
                        cursor: "pointer",
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                      onClick={(e) => previewVideo(e, item)}
                    >
                      <video
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        preload="metadata"
                        muted
                      >
                        <source src={item.url} type="video/mp4" />
                      </video>
                      <PlayArrowOutlined
                        sx={{
                          position: "absolute",
                          fontSize: 20,
                          color: "white",
                          backgroundColor: "rgba(0,0,0,0.6)",
                          borderRadius: "50%",
                          p: 0.5,
                        }}
                      />
                    </Box>
                  ) : item.status === "generating" ? (
                    <Box
                      sx={{
                        width: 80,
                        height: 45,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PendingOutlined sx={{ fontSize: 16, color: "grey.500" }} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 80,
                        height: 45,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <VideoLibraryOutlined sx={{ fontSize: 16, color: "grey.500" }} />
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={item.prompt || ""} arrow>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {item.prompt}
                      </Typography>
                      {item.prompt && (
                        <IconButton
                          size="small"
                          onClick={() => copyPromptToClipboard(item.prompt)}
                          sx={{ opacity: 0.7, "&:hover": { opacity: 1 } }}
                        >
                          <ContentCopyOutlined fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      videoModels.find(m => m.id === item.model)?.name ||
                      item.model ||
                      item.provider ||
                      "Unknown"
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {item.duration ? `${item.duration}s` : "N/A"}
                </TableCell>
                <TableCell>{getStatusChip(item.status)}</TableCell>
                <TableCell>
                  {formatTimestamp(item.created).fullDateTime}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuClick(e, item)}
                    size="small"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={videoDialogOpen} onClose={() => setVideoDialogOpen(false)}>
        <DialogTitle>
          Create Video
          <IconButton
            aria-label="close"
            onClick={() => setVideoDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Create a video using AI-powered fal.ai models. Choose your model and customize your video.
          </DialogContentText>

          {videoModels.length === 0 ? (
            <Box
              sx={{ mt: 2, p: 2, bgcolor: "warning.light", borderRadius: 1 }}
            >
              <Typography variant="body2">
                No video models available. Please contact support at {getSupportContactInfo(whitelabelSettings)}.
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Model Selection */}
              <FormControl fullWidth style={{ marginTop: 20 }}>
                <InputLabel>Video Model</InputLabel>
                <Select
                  label="Video Model"
                  value={selectedModel}
                  onChange={(e) => handleModelChange(e.target.value)}
                >
                  {videoModels.map((model: any, index: number) => (
                    <MenuItem value={model.id} key={index}>
                      <Box sx={{ display: "flex", flexDirection: "column", width: "100%", py: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                          {model.name} - Max {model.max_duration}s ({model.cost_credits} credits)
                        </Box>
                        {model.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            {model.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedModel && (
                <>
                  {/* Single Image Selection for Image-to-Video Models */}
                  {requiresImage() && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Source Image (Required)
                      </Typography>
                      {singleImageUrl ? (
                        <Card sx={{ position: "relative", maxWidth: 400, mx: "auto" }}>
                          <CardMedia
                            component="img"
                            height="200"
                            image={singleImageUrl}
                            alt="Source image"
                            sx={{ objectFit: "cover" }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              bgcolor: "background.paper",
                              "&:hover": { bgcolor: "background.paper" },
                            }}
                            onClick={() => {
                              setSingleImageUrl("");
                              setSingleImageId("");
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Card>
                      ) : (
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<ImageOutlined />}
                          onClick={() => handleOpenImagePicker("single")}
                          sx={{ height: 200, borderStyle: "dashed", maxWidth: 400, mx: "auto" }}
                        >
                          Select Image to Animate
                        </Button>
                      )}
                    </Box>
                  )}

                  {/* Frame Selection for First-Last-Frame Models */}
                  {requiresFrames() && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Frame Images (Required)
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        {/* First Frame */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
                            First Frame
                          </Typography>
                          {firstFrameUrl ? (
                            <Card sx={{ position: "relative" }}>
                              <CardMedia
                                component="img"
                                height="140"
                                image={firstFrameUrl}
                                alt="First frame"
                                sx={{ objectFit: "cover" }}
                              />
                              <IconButton
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  bgcolor: "background.paper",
                                  "&:hover": { bgcolor: "background.paper" },
                                }}
                                onClick={() => {
                                  setFirstFrameUrl("");
                                  setFirstFrameId("");
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Card>
                          ) : (
                            <Button
                              variant="outlined"
                              fullWidth
                              startIcon={<ImageOutlined />}
                              onClick={() => handleOpenImagePicker("first")}
                              sx={{ height: 140, borderStyle: "dashed" }}
                            >
                              Select First Frame
                            </Button>
                          )}
                        </Box>

                        {/* Last Frame */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
                            Last Frame
                          </Typography>
                          {lastFrameUrl ? (
                            <Card sx={{ position: "relative" }}>
                              <CardMedia
                                component="img"
                                height="140"
                                image={lastFrameUrl}
                                alt="Last frame"
                                sx={{ objectFit: "cover" }}
                              />
                              <IconButton
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  bgcolor: "background.paper",
                                  "&:hover": { bgcolor: "background.paper" },
                                }}
                                onClick={() => {
                                  setLastFrameUrl("");
                                  setLastFrameId("");
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Card>
                          ) : (
                            <Button
                              variant="outlined"
                              fullWidth
                              startIcon={<ImageOutlined />}
                              onClick={() => handleOpenImagePicker("last")}
                              sx={{ height: 140, borderStyle: "dashed" }}
                            >
                              Select Last Frame
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <TextField
                    style={{ marginTop: 20 }}
                    label="Video Prompt"
                    value={videoPrompt}
                    fullWidth
                    multiline
                    rows={3}
                    onChange={(e: any) => setVideoPrompt(e.target.value)}
                    helperText={
                      requiresFrames()
                        ? "Describe how the video should transition between the two frames"
                        : "Describe the video you want to generate (e.g., 'A peaceful sunset over mountains with birds flying')"
                    }
                    placeholder="A beautiful landscape with flowing water..."
                  />

                  {/* Duration - Dropdown for specific valid durations */}
                  <FormControl fullWidth style={{ marginTop: 20 }}>
                    <InputLabel>Duration</InputLabel>
                    <Select
                      label="Duration"
                      value={videoDuration}
                      onChange={(e) => setVideoDuration(e.target.value as number)}
                    >
                      {(() => {
                        const model = videoModels.find(m => m.id === selectedModel);
                        if (!model || !model.durations || model.durations.length === 0) {
                          return null;
                        }

                        // Use the durations array from the model
                        return model.durations.map((duration: string) => {
                          // Parse duration (handle '4s' and '4' formats)
                          const durationValue = parseInt(duration.replace('s', ''));
                          return (
                            <MenuItem value={durationValue} key={durationValue}>
                              {durationValue} seconds
                            </MenuItem>
                          );
                        });
                      })()}
                    </Select>
                  </FormControl>

                  {/* Aspect Ratio */}
                  <FormControl fullWidth style={{ marginTop: 20 }}>
                    <InputLabel>Aspect Ratio</InputLabel>
                    <Select
                      label="Aspect Ratio"
                      value={videoAspectRatio}
                      onChange={(e) => setVideoAspectRatio(e.target.value)}
                    >
                      {(() => {
                        const model = videoModels.find(m => m.id === selectedModel);
                        const ratios = model?.aspect_ratios || ["16:9", "9:16"];
                        return ratios.map((ratio: string) => (
                          <MenuItem value={ratio} key={ratio}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              {ratio === "16:9" && <CropLandscapeOutlined fontSize="small" />}
                              {ratio === "9:16" && <CropPortraitOutlined fontSize="small" />}
                              {ratio === "1:1" && <CropSquareOutlined fontSize="small" />}
                              {ratio === "16:9" ? "16:9 (Landscape)" : ratio === "9:16" ? "9:16 (Portrait)" : ratio === "1:1" ? "1:1 (Square)" : ratio}
                            </Box>
                          </MenuItem>
                        ));
                      })()}
                    </Select>
                  </FormControl>

                  {/* Resolution - For Veo 3.1 Lite */}
                  {selectedModel === "fal-ai/veo3.1/lite" && (
                    <FormControl fullWidth style={{ marginTop: 20 }}>
                      <InputLabel>Resolution</InputLabel>
                      <Select
                        label="Resolution"
                        value={videoResolution}
                        onChange={(e) => setVideoResolution(e.target.value)}
                      >
                        <MenuItem value="720p">720p (1280x720)</MenuItem>
                        <MenuItem value="1080p">1080p (1920x1080)</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  {/* Motion Intensity - For Kling models */}
                  {selectedModel?.includes("kling") && (
                    <FormControl fullWidth style={{ marginTop: 20 }}>
                      <InputLabel>Motion Intensity</InputLabel>
                      <Select
                        label="Motion Intensity"
                        value={motionIntensity}
                        onChange={(e) => setMotionIntensity(e.target.value)}
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  {/* Camera Motion - For Kling models */}
                  {selectedModel?.includes("kling") && (
                    <FormControl fullWidth style={{ marginTop: 20 }}>
                      <InputLabel>Camera Motion</InputLabel>
                      <Select
                        label="Camera Motion"
                        value={cameraMotion}
                        onChange={(e) => setCameraMotion(e.target.value)}
                      >
                        <MenuItem value="static">Static</MenuItem>
                        <MenuItem value="pan">Pan</MenuItem>
                        <MenuItem value="zoom">Zoom</MenuItem>
                        <MenuItem value="dolly">Dolly</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  {/* Style - For Kling models */}
                  {selectedModel?.includes("kling") && (
                    <FormControl fullWidth style={{ marginTop: 20 }}>
                      <InputLabel>Style</InputLabel>
                      <Select
                        label="Style"
                        value={videoStyle}
                        onChange={(e) => setVideoStyle(e.target.value)}
                      >
                        <MenuItem value="realistic">Realistic</MenuItem>
                        <MenuItem value="cinematic">Cinematic</MenuItem>
                        <MenuItem value="animated">Animated</MenuItem>
                        <MenuItem value="artistic">Artistic</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Chip
            label={selectedModel ? `${calculateCreditCost()} Credits` : "Select Model"}
            style={{ marginRight: "auto" }}
          />
          <Button onClick={() => setVideoDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={createVideo}
            loading={videoDialogLoading}
            disabled={
              !selectedModel ||
              !videoPrompt.trim() ||
              (requiresFrames() && (!firstFrameUrl || !lastFrameUrl)) ||
              (requiresImage() && !singleImageUrl)
            }
          >
            Create Video
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth="lg"
        open={videoPreviewOpen}
        onClose={() => setVideoPreviewOpen(false)}
      >
        <DialogTitle>Video Preview</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Prompt: {selectedVideo.prompt}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Duration: {selectedVideo.duration}s
          </Typography>
          {selectedVideo.url && (
            <video
              controls
              style={{ width: "100%", maxWidth: "800px" }}
              preload="metadata"
            >
              <source src={selectedVideo.url} type="video/mp4" />
              Your browser does not support the video element.
            </video>
          )}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {selectedVideoForMenu?.status === "completed" &&
          selectedVideoForMenu?.url && (
            <MenuItem onClick={handlePreviewFromMenu}>
              <ListItemIcon>
                <PlayArrowOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Preview Video</ListItemText>
            </MenuItem>
          )}
        {selectedVideoForMenu?.status === "completed" &&
          selectedVideoForMenu?.url && (
            <MenuItem onClick={handleDownloadFromMenu}>
              <ListItemIcon>
                <DownloadOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download</ListItemText>
            </MenuItem>
          )}
        {selectedVideoForMenu?.status === "generating" && (
          <MenuItem disabled>
            <ListItemIcon>
              <PendingOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Generating...</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteFromMenu}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Image Picker Dialog */}
      <ImagePicker
        open={imagePickerOpen}
        onClose={() => {
          setImagePickerOpen(false);
          setSelectingFrame(null);
        }}
        onSelect={handleImageSelect}
        getToken={getToken}
        companyId={companyId}
      />
    </Box>
  );
}
